import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { useEffect, useRef, useState } from "react";
import { api, q } from "../../api.ts";
import { notify } from "../../feedback.ts";
import { titleOf } from "../../inbox-items.ts";
import { DATA_SETUP_LABELS as D, INBOX_LABELS as I, reasonText } from "../../labels.ts";
import { ActionLaunchControl } from "../../local-launcher.tsx";
import { Button, Callout, ErrorText, Kbd, Pill, Section } from "../../ui.tsx";
import { ProductLabel } from "../../ui/product-label.tsx";
import { ServiceLogo, type ServiceBrand } from "../../ui/service-logo.tsx";
import { Select } from "../../ui/select.tsx";
import { Excerpt } from "../../ui/review.tsx";
import { Skeleton } from "../../ui/skeleton.tsx";
import { StatusIcon } from "../../ui/status.tsx";
import { DetailPanelShell, Review, useInbox } from "./shared.tsx";

// Part of the inbox (docs/design-system.md §0.4j; docs/product-decisions.md
// 2026-10-31): the data a product needs after the first read. Execute hands
// it to the person's agent; the agent's proposal is applied here, in the
// person's session, and one that stopped partway is finished here. While a
// proposal is open the only next step is deciding it, so Execute is hidden. A person
// may also say which metric they already have answers a need. Nothing is
// hidden: each item says what it creates.

const LOGO: Partial<Record<C.DataSetup["needs"][number]["platforms"][number]["platform"], ServiceBrand>> = { github: "github", npm: "npm", pypi: "pypi", posthog: "posthog" };

/** What applying an item creates, in plain words. */
export function itemText(item: C.DataSetupItem): string {
  const m = item.metric;
  if ("existing" in m) return D.item.existing(m.existing);
  if ("csv" in m) return D.item.csv(m.csv.name, m.csv.platform === "other" ? m.csv.source_name ?? D.platform.other : D.platform[m.csv.platform] ?? m.csv.platform);
  if (m.collector === "github_stars") return D.item.github_stars;
  if (m.collector === "event_count") return D.item.event_count(m.event);
  if (m.collector === "posthog") return D.item.posthog(m.event, m.aggregation === "unique_users");
  return D.item[m.collector](m.package);
}

/** A recommended need by name; an item without one is a metric the person asked for in their own words. */
const needName = (need: string | null) => need ? D.need[need] ?? need : D.ownMetric;

/** The metrics a person may say answer a need: of its kind, from a source that counts it, not another need's binding. */
function candidates(setup: C.DataSetup, need: C.DataSetup["needs"][number]) {
  const boundElsewhere = new Set(setup.needs.filter((n) => n.need !== need.need && n.answered_by === "binding").map((n) => n.metric_key));
  return setup.metrics.filter((m) => (need.metric_kind === "money" ? m.kind === "money" : m.kind !== "money") && !boundElsewhere.has(m.key)
    && (!m.collector || need.platforms.some((platform) => COLLECTORS[platform.platform]?.includes(m.collector!))));
}
const COLLECTORS: Partial<Record<C.DataSetup["needs"][number]["platforms"][number]["platform"], string[]>> = {
  github: ["github_stars"], npm: ["npm_downloads"], pypi: ["pypi_downloads"], posthog: ["posthog"], zooi_events: ["event_count"],
};

function Needs({ setup, busy, onBind }: { setup: C.DataSetup; busy: boolean; onBind: (need: string, key: string | null) => void }) {
  return <ul className="z-data-needs">
    {setup.needs.map((need) => {
      const options = candidates(setup, need);
      return <li key={need.need} className="z-data-need">
      <div className="z-data-need-head">
        <StatusIcon kind={need.metric_key ? "done" : "not_started"} label={need.metric_key ? D.connected : D.missing} />
        <strong>{D.need[need.need] ?? need.need}</strong>
        <span className="z-muted">{need.importance === "required" ? D.required : D.recommended}</span>
        <span className="z-data-need-state">{need.metric_key ? <>{D.answeredBy(setup.metrics.find((m) => m.key === need.metric_key)?.name ?? need.metric_key)}{need.answered_by && <span className="z-muted"> · {D.by[need.answered_by]}</span>}</> : D.missing}</span>
        {need.metric_key && <Button tone="ghost" size="sm" disabled={busy} title={D.unlinkNote} onClick={() => onBind(need.need, null)}>{D.unlink}</Button>}
      </div>
      {!need.metric_key && options.length > 0 && <div className="z-data-bind">
        <Select label={D.useExisting} value="" options={[{ value: "", label: D.useExisting }, ...options.map((m) => ({ value: m.key, label: m.name }))]} disabled={busy} onValueChange={(key) => { if (key) onBind(need.need, key); }} />
      </div>}
      {!need.metric_key && <ul className="z-data-platforms">
        {need.platforms.map((platform) => <li key={platform.platform}>
          <span className="z-data-platform">
            {LOGO[platform.platform] && <ServiceLogo service={LOGO[platform.platform]!} />}
            {D.platform[platform.platform] ?? platform.platform}
            <span className="z-muted"> · {platform.access === "csv" ? D.csv : platform.connected ? D.readable : D.afterSteps}</span>
          </span>
          {platform.person_steps.length > 0 && !platform.connected && <ol className="z-data-steps" aria-label={D.yourSteps}>
            {platform.person_steps.map((step) => <li key={step.step}>{step.url
              ? <a href={step.url} target={step.url.startsWith(window.location.origin) ? undefined : "_blank"} rel="noreferrer noopener">{D.step[step.step] ?? step.step}</a>
              : D.step[step.step] ?? step.step}</li>)}
          </ol>}
        </li>)}
      </ul>}
    </li>;
    })}
  </ul>;
}

export function DataSetupReview() {
  const { dataProductId } = useParams({ strict: false }) as { dataProductId: string };
  const { items, intent, done } = useInbox();
  const client = useQueryClient();
  const item = items.find((entry) => entry.key === `data_setup:${dataProductId}`);
  const setup = useQuery(q.dataSetup(dataProductId));
  const [busy, setBusy] = useState<"apply" | "reject" | "dismiss" | "bind" | null>(null);
  const [error, setError] = useState<unknown>();
  const applyRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { setError(undefined); }, [dataProductId]);
  useEffect(() => { if ((setup.data?.proposal?.status === "proposed" || setup.data?.proposal?.status === "applying") && intent === "approve") applyRef.current?.focus(); }, [intent, setup.data]);

  if (setup.isLoading) return <DetailPanelShell><Skeleton shape="panel" /></DetailPanelShell>;
  if (!setup.data) return <DetailPanelShell><ErrorText error={setup.error} /></DetailPanelShell>;
  const data = setup.data;
  const proposal = data.proposal;
  const open = proposal?.status === "proposed" || proposal?.status === "applying" ? proposal : null;
  const applying = open?.status === "applying";
  const refresh = () => Promise.all([client.invalidateQueries({ queryKey: ["data-setup", dataProductId] }), client.invalidateQueries({ queryKey: ["metrics", dataProductId] }), client.invalidateQueries({ queryKey: ["workspace-dashboard"] }), client.invalidateQueries({ queryKey: ["metric-collectors", dataProductId] })]);
  const decide = async (decision: "apply" | "reject") => {
    if (!open || (applying && decision === "reject")) return;
    setBusy(decision); setError(undefined);
    try {
      await api.post(`/api/products/${encodeURIComponent(data.product_id)}/data-setup/proposals/${encodeURIComponent(open.id)}/decision`, { decision });
      await refresh();
      // A rejection moves on; an application stays to show what it created.
      if (decision === "reject") done(D.rejectedDone);
      else { notify.success(D.appliedDone); void client.invalidateQueries({ queryKey: ["inbox"] }); }
    } catch (cause) { setError(cause); }
    finally { setBusy(null); }
  };
  const bind = async (need: string, key: string | null) => {
    setBusy("bind"); setError(undefined);
    try {
      await api.put(`/api/products/${encodeURIComponent(data.product_id)}/data-setup/bindings`, { need, metric_key: key });
      await refresh();
      void client.invalidateQueries({ queryKey: ["inbox"] });
      notify.success(key ? D.linked : D.unlinked);
    } catch (cause) { setError(cause); }
    finally { setBusy(null); }
  };
  const dismiss = async (dismissed: boolean) => {
    setBusy("dismiss"); setError(undefined);
    try {
      await api.put(`/api/products/${encodeURIComponent(data.product_id)}/data-setup/dismissed`, { dismissed });
      await refresh();
      if (dismissed) done(D.dismissed);
    } catch (cause) { setError(cause); }
    finally { setBusy(null); }
  };
  return <Review label={I.detail} title={item ? titleOf(item) : D.title(data.product_name)}
    meta={<>{item && <Pill tone="amber" status>{I.reason[item.reason.code] ?? item.reason.code}</Pill>}{item && <ProductLabel product={item.product} />}</>}
    actions={open ? <>
      <Button ref={applyRef} tone="primary" busy={busy === "apply"} disabled={busy !== null} onClick={() => void decide("apply")}>{applying ? D.continueApplying : D.apply} <Kbd>A</Kbd></Button>
      {!applying && <Button tone="soft" busy={busy === "reject"} disabled={busy !== null} onClick={() => void decide("reject")}>{D.reject}</Button>}
    </> : undefined}>
    <p>{data.profile_known ? D.profile[data.profile] : D.profileUnknown}</p>
    {!open && <>
      <p className="z-muted">{D.intro}</p>
      <div className="z-inline"><ActionLaunchControl productId={data.product_id} task="data_setup" compact /></div>
    </>}
    {open && !applying && <p className="z-muted">{D.introProposed}</p>}

    {open && <Section title={D.proposal} note={D.proposedAt(open.created_at)}>
      {applying && <Callout tone="warn">{D.applyingNote}</Callout>}
      <ul className="z-data-proposal">
        {open.items.map((entry, index) => {
          const done = open.result?.items[index];
          return <li key={index}>
            <div>{done && <StatusIcon kind={done.metric_key ? "done" : "failed"} label={done.metric_key ? D.applied : D.failed} />} <strong>{needName(entry.need)}</strong> · {itemText(entry)}</div>
            {done?.error && <p className="z-small">{reasonText({ code: done.error })}</p>}
            <Excerpt className="z-agent-text" lines={2}>{entry.why}</Excerpt>
          </li>;
        })}
      </ul>
      <p className="z-small z-muted">{open.dashboard ? D.addsToOverview : D.notToOverview}</p>
    </Section>}

    {proposal?.status === "applied" && proposal.result && <Section title={D.result}>
      <ul className="z-data-proposal z-data-result">
        {proposal.result.items.map((entry, index) => <li key={index}>
          <StatusIcon kind={entry.metric_key ? "done" : "failed"} label={entry.metric_key ? D.applied : D.failed} />
          <span><strong>{needName(entry.need)}</strong> · {entry.metric_key ? D.created(entry.metric_key) : reasonText({ code: entry.error ?? "internal_error" })}</span>
        </li>)}
      </ul>
      <p className="z-small z-muted">{D.dashboard[proposal.result.dashboard]}</p>
    </Section>}

    <Section title={D.needs}>
      <Needs setup={data} busy={busy !== null} onBind={(need, key) => void bind(need, key)} />
      <p className="z-small z-muted">{D.keyNote}</p>
    </Section>

    <ErrorText error={error} />
    <p className="z-inline">
      <Link to="/p/$productId/data" params={{ productId: data.product_id }} search={{ view: "sources" }}>{D.openData}</Link>
      {data.dismissed
        ? <Button tone="ghost" disabled={busy !== null} onClick={() => void dismiss(false)}>{D.restore}</Button>
        : <Button tone="ghost" disabled={busy !== null} title={D.dismissNote} onClick={() => void dismiss(true)}>{D.dismiss}</Button>}
    </p>
  </Review>;
}
